import { useState, useEffect } from 'react';
import { Socket } from 'socket.io-client';
import toast from 'react-hot-toast';

interface BattlePanelProps {
  sessionId: string;
  socket: Socket | null;
  isConnected: boolean;
}

interface BattleCombatant {
  id: string;
  name: string;
  damage: number;
  maxHP: number;
  currentHP: number;
  type: 'hero' | 'enemy' | 'ally';
  teamColor?: string;
  characterId?: string;
}

interface CharacterInfo {
  id: string;
  name: string;
  team: string;
  damage?: number;
  maxHP?: number;
  currentHP?: number;
}

type PanelState = 'setup' | 'active';

const TEAM_COLORS_STYLE: Record<string, string> = {
  BLUE: 'bg-blue-600',
  RED: 'bg-red-600',
  GREEN: 'bg-green-600',
  YELLOW: 'bg-yellow-600',
};

const TEAM_COLORS_HEX: Record<string, string> = {
  BLUE: '#3b82f6',
  RED: '#ef4444',
  GREEN: '#22c55e',
  YELLOW: '#eab308',
};

const TEAM_NAMES: Record<string, string> = {
  BLUE: 'Blu',
  RED: 'Rossa',
  GREEN: 'Verde',
  YELLOW: 'Gialla',
};

const BATTLE_EFFECTS = [
  { type: 'fireball', label: 'Palla di Fuoco', icon: '🔥', buttonColor: 'bg-orange-600 hover:bg-orange-500' },
  { type: 'resurrection', label: 'Resurrezione', icon: '✨', buttonColor: 'bg-yellow-600 hover:bg-yellow-500' },
  { type: 'transformation', label: 'Trasformazione', icon: '🌀', buttonColor: 'bg-green-600 hover:bg-green-500' },
  { type: 'divine-light', label: 'Luce Divina', icon: '🌟', buttonColor: 'bg-amber-500 hover:bg-amber-400' },
  { type: 'sleep', label: 'Sonno', icon: '😴', buttonColor: 'bg-purple-600 hover:bg-purple-500' },
  { type: 'magic-missiles', label: 'Dardi Incantati', icon: '⚡', buttonColor: 'bg-blue-600 hover:bg-blue-500' },
  { type: 'poison', label: 'Avvelenamento', icon: '☠️', buttonColor: 'bg-lime-700 hover:bg-lime-600' },
  { type: 'battle-cry', label: 'Tuono', icon: '📯', buttonColor: 'bg-red-700 hover:bg-red-600' },
  { type: 'druid-summon', label: 'Druido Evoca', icon: '🌿', buttonColor: 'bg-emerald-700 hover:bg-emerald-600' },
  { type: 'bard', label: 'Bardo', icon: '🎵', buttonColor: 'bg-yellow-700 hover:bg-yellow-600' },
] as const;

export function BattlePanel({ sessionId, socket }: BattlePanelProps) {
  const [panelState, setPanelState] = useState<PanelState>('setup');
  const [activeBattleId, setActiveBattleId] = useState<string | null>(null);

  // Setup state
  const [availableCharacters, setAvailableCharacters] = useState<CharacterInfo[]>([]);
  const [selectedHeroes, setSelectedHeroes] = useState<BattleCombatant[]>([]);
  const [enemies, setEnemies] = useState<BattleCombatant[]>([]);

  // New ally form
  const [newAllyName, setNewAllyName] = useState('');
  const [newAllyDamage, setNewAllyDamage] = useState(4);
  const [newAllyMaxHP, setNewAllyMaxHP] = useState(20);
  const [newAllyCurrentHP, setNewAllyCurrentHP] = useState(20);

  // New enemy form
  const [newEnemyName, setNewEnemyName] = useState('');
  const [newEnemyDamage, setNewEnemyDamage] = useState(6);
  const [newEnemyMaxHP, setNewEnemyMaxHP] = useState(10);
  const [newEnemyCurrentHP, setNewEnemyCurrentHP] = useState(10);

  // Active battle state
  const [combatants, setCombatants] = useState<BattleCombatant[]>([]);
  const [selectedTargetId, setSelectedTargetId] = useState<string | null>(null);
  const [damageInput, setDamageInput] = useState('');
  const [battleOutcome, setBattleOutcome] = useState<'victory' | 'defeat' | 'draw' | null>(null);

  // HP management (fuori battaglia)
  const [selectedHeroId, setSelectedHeroId] = useState<string | null>(null);
  const [hpInput, setHpInput] = useState('');

  // Fetch characters on mount
  useEffect(() => {
    fetchCharacters();
    checkActiveBattle();
  }, [sessionId]);

  // Socket listeners
  useEffect(() => {
    if (!socket) return;

    const handleStateUpdate = (data: { battleId: string; combatants: BattleCombatant[] }) => {
      if (data.battleId === activeBattleId) {
        setCombatants(data.combatants);
      }
    };

    const handleBattleEnded = (data: { battleId: string }) => {
      if (data.battleId === activeBattleId) {
        setPanelState('setup');
        setActiveBattleId(null);
        setCombatants([]);
        setSelectedTargetId(null);
        setBattleOutcome(null);
        setDamageInput('');
        setEnemies([]);
        toast.success('Battaglia terminata');
        // Ri-fetch personaggi per HP aggiornati
        fetchCharacters();
      }
    };

    const handleOutcome = (data: { battleId: string; outcome: 'victory' | 'defeat' | 'draw' }) => {
      if (data.battleId === activeBattleId) {
        setBattleOutcome(data.outcome);
      }
    };

    const handleHeroHPUpdate = (data: { characterId: string; currentHP: number; maxHP: number }) => {
      setAvailableCharacters((prev) =>
        prev.map((c) =>
          c.id === data.characterId ? { ...c, currentHP: data.currentHP, maxHP: data.maxHP } : c
        )
      );
      setSelectedHeroes((prev) =>
        prev.map((h) =>
          h.characterId === data.characterId ? { ...h, currentHP: data.currentHP, maxHP: data.maxHP } : h
        )
      );
    };

    socket.on('battle:state-update', handleStateUpdate);
    socket.on('battle:ended', handleBattleEnded);
    socket.on('battle:outcome', handleOutcome);
    socket.on('hero:hp-update', handleHeroHPUpdate);

    return () => {
      socket.off('battle:state-update', handleStateUpdate);
      socket.off('battle:ended', handleBattleEnded);
      socket.off('battle:outcome', handleOutcome);
      socket.off('hero:hp-update', handleHeroHPUpdate);
    };
  }, [socket, activeBattleId]);

  const fetchCharacters = async () => {
    try {
      // Stessa query che mostra gli eroi votati (funziona già correttamente)
      const response = await fetch(`/api/sheet-votes/session/${sessionId}/latest`);
      if (!response.ok) return;
      const data = await response.json();
      const votes = data.votes || [];

      // Prendi i Character della sessione per gli HP attuali
      const charsRes = await fetch(`/api/sessions/${sessionId}/characters`);
      const charsData = charsRes.ok ? await charsRes.json() : { characters: [] };
      // Mappa sheetId:team → Character più recente (updatedAt)
      const charByKey = new Map<string, any>();
      const sortedChars = [...(charsData.characters || [])].sort(
        (a: any, b: any) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()
      );
      sortedChars.forEach((c: any) => charByKey.set(`${c.sheetId}:${c.team}`, c));

      const chars: CharacterInfo[] = votes
        .filter((v: any) => v.winnerSheet)
        .map((v: any) => {
          const character = charByKey.get(`${v.winnerSheet.id}:${v.teamColor}`);
          return {
            id: character?.id || v.winnerSheet.id,
            name: v.winnerSheet.name,
            team: v.teamColor,
            damage: character?.damage || v.winnerSheet.damage || 6,
            maxHP: character?.maxHP || v.winnerSheet.maxHP || 20,
            currentHP: character?.currentHP,
          };
        });

      setAvailableCharacters(chars);

      // Seleziona tutti gli eroi di default
      setSelectedHeroes(chars.map((char) => ({
        id: '',
        name: char.name,
        damage: char.damage || 6,
        maxHP: char.maxHP || 20,
        currentHP: char.currentHP ?? char.maxHP ?? 20,
        type: 'hero' as const,
        teamColor: char.team,
        characterId: char.id,
      })));
    } catch (error) {
      console.error('[BattlePanel] Error fetching characters:', error);
    }
  };

  const checkActiveBattle = async () => {
    try {
      const response = await fetch(`/api/battles/session/${sessionId}/active`);
      if (response.ok) {
        const data = await response.json();
        if (data.battle) {
          setActiveBattleId(data.battle.id);
          setCombatants(data.battle.combatants);
          setPanelState('active');
        }
      }
    } catch (error) {
      console.error('[BattlePanel] Error checking active battle:', error);
    }
  };

  const addCombatantLive = async (combatant: Omit<BattleCombatant, 'id'> & { id?: string }) => {
    if (!activeBattleId) return;
    try {
      const response = await fetch(`/api/battles/${activeBattleId}/add-combatant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ combatant }),
      });
      if (response.ok) {
        toast.success(`${combatant.name} aggiunto alla battaglia`);
      } else {
        const error = await response.json();
        toast.error(error.error || 'Errore aggiunta combattente');
      }
    } catch (error) {
      console.error('[BattlePanel] Error adding combatant live:', error);
      toast.error('Errore aggiunta combattente');
    }
  };

  const removeCombatantLive = async (combatantId: string, name: string) => {
    if (!activeBattleId) return;
    try {
      const response = await fetch(`/api/battles/${activeBattleId}/remove-combatant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ combatantId }),
      });
      if (response.ok) {
        if (selectedTargetId === combatantId) setSelectedTargetId(null);
        toast.success(`${name} rimosso dalla battaglia`);
      } else {
        const error = await response.json();
        toast.error(error.error || 'Errore rimozione combattente');
      }
    } catch (error) {
      console.error('[BattlePanel] Error removing combatant live:', error);
      toast.error('Errore rimozione combattente');
    }
  };

  const addHeroFromCharacter = (char: CharacterInfo) => {
    const combatant = {
      id: '',
      name: char.name,
      damage: char.damage || 6,
      maxHP: char.maxHP || 20,
      currentHP: char.currentHP ?? char.maxHP ?? 20,
      type: 'hero' as const,
      teamColor: char.team,
      characterId: char.id,
    };

    if (panelState === 'active') {
      // Evita duplicati controllando combatants attivi
      if (combatants.some((c) => c.characterId === char.id)) {
        toast.error('Eroe già in battaglia');
        return;
      }
      addCombatantLive(combatant);
    } else {
      if (selectedHeroes.some((h) => h.characterId === char.id)) {
        toast.error('Eroe già aggiunto');
        return;
      }
      setSelectedHeroes((prev) => [...prev, combatant]);
    }
  };

  const removeHero = (index: number) => {
    setSelectedHeroes((prev) => prev.filter((_, i) => i !== index));
  };

  const addAlly = () => {
    if (!newAllyName.trim()) {
      toast.error('Inserisci il nome dell\'alleato');
      return;
    }
    const combatant = {
      id: '',
      name: newAllyName.trim(),
      damage: newAllyDamage,
      maxHP: newAllyMaxHP,
      currentHP: newAllyCurrentHP,
      type: 'ally' as const,
    };

    if (panelState === 'active') {
      addCombatantLive(combatant);
    } else {
      setSelectedHeroes((prev) => [...prev, combatant]);
    }
    setNewAllyName('');
    setNewAllyDamage(4);
    setNewAllyMaxHP(20);
    setNewAllyCurrentHP(20);
  };

  const addEnemy = () => {
    if (!newEnemyName.trim()) {
      toast.error('Inserisci il nome del nemico');
      return;
    }
    const combatant = {
      id: '',
      name: newEnemyName.trim(),
      damage: newEnemyDamage,
      maxHP: newEnemyMaxHP,
      currentHP: newEnemyCurrentHP,
      type: 'enemy' as const,
    };

    if (panelState === 'active') {
      addCombatantLive(combatant);
    } else {
      setEnemies((prev) => [...prev, combatant]);
    }
    setNewEnemyName('');
    setNewEnemyDamage(6);
    setNewEnemyMaxHP(10);
    setNewEnemyCurrentHP(10);
  };

  const removeEnemy = (index: number) => {
    setEnemies((prev) => prev.filter((_, i) => i !== index));
  };

  const startBattle = async () => {
    if (selectedHeroes.length === 0 && enemies.length === 0) {
      toast.error('Aggiungi almeno un combattente');
      return;
    }

    try {
      const allCombatants = [...selectedHeroes, ...enemies];
      const response = await fetch('/api/battles/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, combatants: allCombatants }),
      });

      if (response.ok) {
        const data = await response.json();
        setActiveBattleId(data.battle.id);
        setCombatants(data.battle.combatants);
        setPanelState('active');
        toast.success('Battaglia iniziata!');
      } else {
        const error = await response.json();
        toast.error(error.error || 'Errore avvio battaglia');
      }
    } catch (error) {
      console.error('[BattlePanel] Error starting battle:', error);
      toast.error('Errore avvio battaglia');
    }
  };

  const applyDamage = async () => {
    if (!selectedTargetId) {
      toast.error('Seleziona un personaggio');
      return;
    }
    const dmg = parseInt(damageInput);
    if (isNaN(dmg) || dmg <= 0) {
      toast.error('Inserisci un danno valido');
      return;
    }

    try {
      const response = await fetch(`/api/battles/${activeBattleId}/damage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetId: selectedTargetId, damage: dmg }),
      });

      if (response.ok) {
        setDamageInput('');
        const target = combatants.find((c) => c.id === selectedTargetId);
        toast.success(`${dmg} danni a ${target?.name || 'personaggio'}`);
      } else {
        const error = await response.json();
        toast.error(error.error || 'Errore applicazione danno');
      }
    } catch (error) {
      console.error('[BattlePanel] Error applying damage:', error);
      toast.error('Errore applicazione danno');
    }
  };

  const applyHeal = async () => {
    if (!selectedTargetId) {
      toast.error('Seleziona un personaggio');
      return;
    }
    const heal = parseInt(damageInput);
    if (isNaN(heal) || heal <= 0) {
      toast.error('Inserisci un valore valido');
      return;
    }

    try {
      const response = await fetch(`/api/battles/${activeBattleId}/heal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetId: selectedTargetId, amount: heal }),
      });

      if (response.ok) {
        setDamageInput('');
        const target = combatants.find((c) => c.id === selectedTargetId);
        toast.success(`+${heal} HP a ${target?.name || 'personaggio'}`);
      } else {
        const error = await response.json();
        toast.error(error.error || 'Errore applicazione cura');
      }
    } catch (error) {
      console.error('[BattlePanel] Error applying heal:', error);
      toast.error('Errore applicazione cura');
    }
  };

  const setOutcome = async (outcome: 'victory' | 'defeat' | 'draw') => {
    try {
      const response = await fetch(`/api/battles/${activeBattleId}/outcome`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ outcome }),
      });

      if (response.ok) {
        setBattleOutcome(outcome);
        const labels = { victory: 'Vittoria', defeat: 'Sconfitta', draw: 'Pareggio' };
        toast.success(`Esito: ${labels[outcome]}`);
      } else {
        const error = await response.json();
        toast.error(error.error || 'Errore impostazione esito');
      }
    } catch (error) {
      console.error('[BattlePanel] Error setting outcome:', error);
      toast.error('Errore impostazione esito');
    }
  };

  const closeBattle = async () => {
    try {
      await fetch(`/api/battles/${activeBattleId}/end`, { method: 'POST' });
    } catch (error) {
      console.error('[BattlePanel] Error ending battle:', error);
      toast.error('Errore chiusura battaglia');
    }
  };

  const applyHeroHP = async (type: 'damage' | 'heal') => {
    if (!selectedHeroId) {
      toast.error('Seleziona un eroe');
      return;
    }
    const value = parseInt(hpInput);
    if (isNaN(value) || value <= 0) {
      toast.error('Inserisci un valore valido');
      return;
    }

    try {
      const body: any = { sessionId };
      if (type === 'damage') body.damage = value;
      else body.heal = value;

      const response = await fetch(`/api/battles/characters/${selectedHeroId}/hp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        setHpInput('');
        const hero = availableCharacters.find((c) => c.id === selectedHeroId);
        toast.success(
          type === 'damage'
            ? `${value} danni a ${hero?.name || 'eroe'}`
            : `+${value} HP a ${hero?.name || 'eroe'}`
        );
      } else {
        const error = await response.json();
        toast.error(error.error || 'Errore aggiornamento HP');
      }
    } catch (error) {
      console.error('[BattlePanel] Error applying HP:', error);
      toast.error('Errore aggiornamento HP');
    }
  };

  const triggerEffect = (effectType: string) => {
    if (!socket) {
      toast.error('Socket non connesso');
      return;
    }
    socket.emit('projection:trigger-effect', { sessionId, effectType });
    toast.success('Effetto inviato!', { icon: '✨', duration: 2000 });
  };

  // ---- SETUP VIEW ----
  if (panelState === 'setup') {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Left: Heroes */}
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <h3 className="text-lg font-bold text-white mb-3">⚔️ Eroi e Alleati</h3>

            {/* Available characters */}
            {availableCharacters.length > 0 && (
              <div className="mb-4">
                <p className="text-gray-400 text-sm mb-2">Eroi disponibili:</p>
                <div className="flex flex-wrap gap-2">
                  {availableCharacters.map((char) => {
                    const isAdded = selectedHeroes.some((h) => h.characterId === char.id);
                    return (
                      <button
                        key={char.id}
                        onClick={() => addHeroFromCharacter(char)}
                        disabled={isAdded}
                        className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all ${
                          isAdded
                            ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                            : `${TEAM_COLORS_STYLE[char.team] || 'bg-gray-600'} text-white hover:opacity-80`
                        }`}
                      >
                        {char.name} ({TEAM_NAMES[char.team] || char.team})
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Selected heroes list */}
            {selectedHeroes.length > 0 && (
              <div className="mb-4 space-y-2">
                <p className="text-gray-400 text-sm">In battaglia:</p>
                {selectedHeroes.map((hero, i) => (
                  <div key={i} className="flex items-center justify-between bg-gray-700 rounded-lg px-3 py-2">
                    <div>
                      <span className="text-white font-semibold text-sm">{hero.name}</span>
                      <span className="text-gray-400 text-xs ml-2">
                        D:{hero.damage} HP:{hero.currentHP}/{hero.maxHP}
                        {hero.type === 'ally' && ' (alleato)'}
                      </span>
                    </div>
                    <button onClick={() => removeHero(i)} className="text-red-400 hover:text-red-300 text-sm">✕</button>
                  </div>
                ))}
              </div>
            )}

            {/* Add ally form */}
            <div className="border-t border-gray-600 pt-3 mt-3">
              <p className="text-gray-400 text-sm mb-2">Aggiungi alleato:</p>
              <div className="flex gap-2 items-end flex-wrap">
                <div className="flex-1 min-w-[120px]">
                  <label className="text-gray-500 text-xs">Nome</label>
                  <input
                    type="text"
                    value={newAllyName}
                    onChange={(e) => setNewAllyName(e.target.value)}
                    className="w-full bg-gray-700 text-white px-2 py-1.5 rounded text-sm border border-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Nome alleato"
                    onKeyDown={(e) => e.key === 'Enter' && addAlly()}
                  />
                </div>
                <div className="w-16">
                  <label className="text-gray-500 text-xs">Danno</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={newAllyDamage}
                    onChange={(e) => setNewAllyDamage(e.target.value as any)}
                    onBlur={(e) => { const n = parseInt(e.target.value); setNewAllyDamage(isNaN(n) ? 4 : Math.max(0, n)); }}
                    className="w-full bg-gray-700 text-white px-2 py-1.5 rounded text-sm border border-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div className="w-16">
                  <label className="text-gray-500 text-xs">HP Max</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={newAllyMaxHP}
                    onChange={(e) => { setNewAllyMaxHP(e.target.value as any); setNewAllyCurrentHP(e.target.value as any); }}
                    onBlur={(e) => { const n = parseInt(e.target.value); const v = isNaN(n) ? 20 : Math.max(1, n); setNewAllyMaxHP(v); setNewAllyCurrentHP(v); }}
                    className="w-full bg-gray-700 text-white px-2 py-1.5 rounded text-sm border border-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div className="w-16">
                  <label className="text-gray-500 text-xs">HP Att.</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={newAllyCurrentHP}
                    onChange={(e) => setNewAllyCurrentHP(e.target.value as any)}
                    onBlur={(e) => { const n = parseInt(e.target.value); setNewAllyCurrentHP(isNaN(n) ? 20 : Math.max(0, n)); }}
                    className="w-full bg-gray-700 text-white px-2 py-1.5 rounded text-sm border border-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <button
                  onClick={addAlly}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm font-semibold hover:bg-blue-500"
                >
                  +
                </button>
              </div>
            </div>
          </div>

          {/* Right: Enemies */}
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <h3 className="text-lg font-bold text-white mb-3">👹 Nemici</h3>

            {/* Enemy list */}
            {enemies.length > 0 && (
              <div className="mb-4 space-y-2">
                {enemies.map((enemy, i) => (
                  <div key={i} className="flex items-center justify-between bg-gray-700 rounded-lg px-3 py-2">
                    <div>
                      <span className="text-white font-semibold text-sm">{enemy.name}</span>
                      <span className="text-gray-400 text-xs ml-2">
                        D:{enemy.damage} HP:{enemy.currentHP}/{enemy.maxHP}
                      </span>
                    </div>
                    <button onClick={() => removeEnemy(i)} className="text-red-400 hover:text-red-300 text-sm">✕</button>
                  </div>
                ))}
              </div>
            )}

            {/* Add enemy form */}
            <div className="border-t border-gray-600 pt-3 mt-3">
              <p className="text-gray-400 text-sm mb-2">Aggiungi nemico:</p>
              <div className="flex gap-2 items-end flex-wrap">
                <div className="flex-1 min-w-[120px]">
                  <label className="text-gray-500 text-xs">Nome</label>
                  <input
                    type="text"
                    value={newEnemyName}
                    onChange={(e) => setNewEnemyName(e.target.value)}
                    className="w-full bg-gray-700 text-white px-2 py-1.5 rounded text-sm border border-gray-600 focus:outline-none focus:ring-1 focus:ring-purple-500"
                    placeholder="Nome nemico"
                    onKeyDown={(e) => e.key === 'Enter' && addEnemy()}
                  />
                </div>
                <div className="w-16">
                  <label className="text-gray-500 text-xs">Danno</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={newEnemyDamage}
                    onChange={(e) => setNewEnemyDamage(e.target.value as any)}
                    onBlur={(e) => { const n = parseInt(e.target.value); setNewEnemyDamage(isNaN(n) ? 6 : Math.max(0, n)); }}
                    className="w-full bg-gray-700 text-white px-2 py-1.5 rounded text-sm border border-gray-600 focus:outline-none focus:ring-1 focus:ring-purple-500"
                  />
                </div>
                <div className="w-16">
                  <label className="text-gray-500 text-xs">HP Max</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={newEnemyMaxHP}
                    onChange={(e) => { setNewEnemyMaxHP(e.target.value as any); setNewEnemyCurrentHP(e.target.value as any); }}
                    onBlur={(e) => { const n = parseInt(e.target.value); const v = isNaN(n) ? 10 : Math.max(1, n); setNewEnemyMaxHP(v); setNewEnemyCurrentHP(v); }}
                    className="w-full bg-gray-700 text-white px-2 py-1.5 rounded text-sm border border-gray-600 focus:outline-none focus:ring-1 focus:ring-purple-500"
                  />
                </div>
                <div className="w-16">
                  <label className="text-gray-500 text-xs">HP Att.</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={newEnemyCurrentHP}
                    onChange={(e) => setNewEnemyCurrentHP(e.target.value as any)}
                    onBlur={(e) => { const n = parseInt(e.target.value); setNewEnemyCurrentHP(isNaN(n) ? 10 : Math.max(0, n)); }}
                    className="w-full bg-gray-700 text-white px-2 py-1.5 rounded text-sm border border-gray-600 focus:outline-none focus:ring-1 focus:ring-purple-500"
                  />
                </div>
                <button
                  onClick={addEnemy}
                  className="px-3 py-1.5 bg-purple-600 text-white rounded text-sm font-semibold hover:bg-purple-500"
                >
                  +
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Start Battle Button */}
        <button
          onClick={startBattle}
          disabled={selectedHeroes.length === 0 && enemies.length === 0}
          className="w-full py-3 bg-red-600 text-white rounded-lg font-bold text-lg hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          ⚔️ Inizia Battaglia
        </button>

        {/* HP Management + Battle Effects */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* HP Management Panel (fuori battaglia) */}
          {availableCharacters.length > 0 && (
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <h3 className="text-lg font-bold text-white mb-3">❤️ Gestione HP Eroi</h3>

              {/* Lista eroi */}
              <div className="space-y-2 mb-4">
                {availableCharacters.map((char) => {
                  const isSelected = selectedHeroId === char.id;
                  const hp = char.currentHP ?? char.maxHP ?? 20;
                  const max = char.maxHP ?? 20;
                  const hpPercent = max > 0 ? (hp / max) * 100 : 0;
                  return (
                    <button
                      key={char.id}
                      onClick={() => setSelectedHeroId(isSelected ? null : char.id)}
                      className={`w-full text-left rounded-lg p-3 transition-all border-2 ${
                        isSelected
                          ? 'border-yellow-400 bg-gray-600'
                          : 'border-transparent bg-gray-700 hover:bg-gray-600'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-white font-semibold text-sm">{char.name}</span>
                        <span className="text-gray-400 text-xs">
                          {TEAM_NAMES[char.team] || char.team}
                        </span>
                      </div>
                      <div className="relative h-4 bg-gray-800 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            width: `${hpPercent}%`,
                            backgroundColor: TEAM_COLORS_HEX[char.team] || '#60a5fa',
                          }}
                        />
                        <span
                          className="absolute inset-0 flex items-center justify-center text-white text-xs font-bold"
                          style={{ textShadow: '0 1px 2px rgba(0,0,0,0.8)' }}
                        >
                          {hp}/{max}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Controlli danni/cure */}
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-gray-400 text-sm font-semibold whitespace-nowrap">
                  {selectedHeroId
                    ? `Bersaglio: ${availableCharacters.find((c) => c.id === selectedHeroId)?.name || '??'}`
                    : 'Seleziona un eroe'}
                </span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={hpInput}
                  onChange={(e) => setHpInput(e.target.value)}
                  placeholder="Valore"
                  className="w-24 bg-gray-700 text-white px-3 py-2 rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                  onKeyDown={(e) => e.key === 'Enter' && applyHeroHP('damage')}
                />
                <button
                  onClick={() => applyHeroHP('damage')}
                  disabled={!selectedHeroId || !hpInput}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  Danni
                </button>
                <button
                  onClick={() => applyHeroHP('heal')}
                  disabled={!selectedHeroId || !hpInput}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  Cura
                </button>
              </div>
            </div>
          )}

          {/* Effetti Magici Panel */}
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <h3 className="text-lg font-bold text-white mb-3">✨ Effetti Magici</h3>
            <div className="grid grid-cols-2 gap-2">
              {BATTLE_EFFECTS.map((effect) => (
                <button
                  key={effect.type}
                  onClick={() => triggerEffect(effect.type)}
                  className={`px-3 py-3 ${effect.buttonColor} text-white rounded-lg text-sm font-semibold transition-all`}
                >
                  {effect.icon} {effect.label}
                </button>
              ))}
            </div>
            <p className="text-gray-500 text-xs mt-3">
              Effetti visivi e sonori sul display del proiettore.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ---- ACTIVE BATTLE VIEW ----
  const heroes = combatants.filter((c) => c.type === 'hero' || c.type === 'ally');
  const activeEnemies = combatants.filter((c) => c.type === 'enemy');

  return (
    <div className="space-y-4">
      {/* Combatant selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Heroes */}
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <h3 className="text-lg font-bold text-white mb-3">⚔️ Eroi</h3>
          <div className="space-y-2">
            {heroes.map((hero) => {
              const isSelected = selectedTargetId === hero.id;
              const isDead = hero.currentHP <= 0;
              const hpPercent = hero.maxHP > 0 ? (hero.currentHP / hero.maxHP) * 100 : 0;
              return (
                <div key={hero.id} className="flex items-center gap-2">
                  <button
                    onClick={() => setSelectedTargetId(isSelected ? null : hero.id)}
                    className={`flex-1 text-left rounded-lg p-3 transition-all border-2 ${
                      isSelected
                        ? 'border-yellow-400 bg-gray-600'
                        : 'border-transparent bg-gray-700 hover:bg-gray-600'
                    } ${isDead ? 'opacity-50' : ''}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-white font-semibold text-sm">{hero.name}</span>
                      <span className="text-gray-400 text-xs">D:{hero.damage}</span>
                    </div>
                    <div className="relative h-4 bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${hpPercent}%`,
                          backgroundColor: TEAM_COLORS_HEX[hero.teamColor || ''] || '#60a5fa',
                        }}
                      />
                      <span className="absolute inset-0 flex items-center justify-center text-white text-xs font-bold" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.8)' }}>
                        {hero.currentHP}/{hero.maxHP}
                      </span>
                    </div>
                  </button>
                  <button
                    onClick={() => removeCombatantLive(hero.id, hero.name)}
                    className="text-red-400 hover:text-red-300 text-sm px-1"
                    title="Rimuovi dalla battaglia"
                  >
                    ✕
                  </button>
                </div>
              );
            })}
          </div>

          {/* Aggiungi eroi/alleati durante battaglia */}
          {availableCharacters.length > 0 && (
            <div className="border-t border-gray-600 pt-3 mt-3">
              <p className="text-gray-400 text-sm mb-2">Aggiungi eroe:</p>
              <div className="flex flex-wrap gap-2">
                {availableCharacters.map((char) => {
                  const isInBattle = combatants.some((c) => c.characterId === char.id);
                  return (
                    <button
                      key={char.id}
                      onClick={() => addHeroFromCharacter(char)}
                      disabled={isInBattle}
                      className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all ${
                        isInBattle
                          ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                          : `${TEAM_COLORS_STYLE[char.team] || 'bg-gray-600'} text-white hover:opacity-80`
                      }`}
                    >
                      {char.name} ({TEAM_NAMES[char.team] || char.team})
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="border-t border-gray-600 pt-3 mt-3">
            <p className="text-gray-400 text-sm mb-2">Aggiungi alleato:</p>
            <div className="flex gap-2 items-end flex-wrap">
              <div className="flex-1 min-w-[120px]">
                <label className="text-gray-500 text-xs">Nome</label>
                <input
                  type="text"
                  value={newAllyName}
                  onChange={(e) => setNewAllyName(e.target.value)}
                  className="w-full bg-gray-700 text-white px-2 py-1.5 rounded text-sm border border-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Nome alleato"
                  onKeyDown={(e) => e.key === 'Enter' && addAlly()}
                />
              </div>
              <div className="w-16">
                <label className="text-gray-500 text-xs">Danno</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={newAllyDamage}
                  onChange={(e) => setNewAllyDamage(e.target.value as any)}
                  onBlur={(e) => { const n = parseInt(e.target.value); setNewAllyDamage(isNaN(n) ? 4 : Math.max(0, n)); }}
                  className="w-full bg-gray-700 text-white px-2 py-1.5 rounded text-sm border border-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div className="w-16">
                <label className="text-gray-500 text-xs">HP Max</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={newAllyMaxHP}
                  onChange={(e) => { setNewAllyMaxHP(e.target.value as any); setNewAllyCurrentHP(e.target.value as any); }}
                  onBlur={(e) => { const n = parseInt(e.target.value); const v = isNaN(n) ? 20 : Math.max(1, n); setNewAllyMaxHP(v); setNewAllyCurrentHP(v); }}
                  className="w-full bg-gray-700 text-white px-2 py-1.5 rounded text-sm border border-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div className="w-16">
                <label className="text-gray-500 text-xs">HP Att.</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={newAllyCurrentHP}
                  onChange={(e) => setNewAllyCurrentHP(e.target.value as any)}
                  onBlur={(e) => { const n = parseInt(e.target.value); setNewAllyCurrentHP(isNaN(n) ? 20 : Math.max(0, n)); }}
                  className="w-full bg-gray-700 text-white px-2 py-1.5 rounded text-sm border border-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <button
                onClick={addAlly}
                className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm font-semibold hover:bg-blue-500"
              >
                +
              </button>
            </div>
          </div>
        </div>

        {/* Enemies */}
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <h3 className="text-lg font-bold text-white mb-3">👹 Nemici</h3>
          <div className="space-y-2">
            {activeEnemies.map((enemy) => {
              const isSelected = selectedTargetId === enemy.id;
              const isDead = enemy.currentHP <= 0;
              const hpPercent = enemy.maxHP > 0 ? (enemy.currentHP / enemy.maxHP) * 100 : 0;
              return (
                <div key={enemy.id} className="flex items-center gap-2">
                  <button
                    onClick={() => setSelectedTargetId(isSelected ? null : enemy.id)}
                    className={`flex-1 text-left rounded-lg p-3 transition-all border-2 ${
                      isSelected
                        ? 'border-yellow-400 bg-gray-600'
                        : 'border-transparent bg-gray-700 hover:bg-gray-600'
                    } ${isDead ? 'opacity-50' : ''}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-white font-semibold text-sm">{enemy.name}</span>
                      <span className="text-gray-400 text-xs">D:{enemy.damage}</span>
                    </div>
                    <div className="relative h-4 bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${hpPercent}%`, backgroundColor: '#a78bfa' }}
                      />
                      <span className="absolute inset-0 flex items-center justify-center text-white text-xs font-bold" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.8)' }}>
                        {enemy.currentHP}/{enemy.maxHP}
                      </span>
                    </div>
                  </button>
                  <button
                    onClick={() => removeCombatantLive(enemy.id, enemy.name)}
                    className="text-red-400 hover:text-red-300 text-sm px-1"
                    title="Rimuovi dalla battaglia"
                  >
                    ✕
                  </button>
                </div>
              );
            })}
          </div>

          {/* Aggiungi nemici durante battaglia */}
          <div className="border-t border-gray-600 pt-3 mt-3">
            <p className="text-gray-400 text-sm mb-2">Aggiungi nemico:</p>
            <div className="flex gap-2 items-end flex-wrap">
              <div className="flex-1 min-w-[120px]">
                <label className="text-gray-500 text-xs">Nome</label>
                <input
                  type="text"
                  value={newEnemyName}
                  onChange={(e) => setNewEnemyName(e.target.value)}
                  className="w-full bg-gray-700 text-white px-2 py-1.5 rounded text-sm border border-gray-600 focus:outline-none focus:ring-1 focus:ring-purple-500"
                  placeholder="Nome nemico"
                  onKeyDown={(e) => e.key === 'Enter' && addEnemy()}
                />
              </div>
              <div className="w-16">
                <label className="text-gray-500 text-xs">Danno</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={newEnemyDamage}
                  onChange={(e) => setNewEnemyDamage(e.target.value as any)}
                  onBlur={(e) => { const n = parseInt(e.target.value); setNewEnemyDamage(isNaN(n) ? 6 : Math.max(0, n)); }}
                  className="w-full bg-gray-700 text-white px-2 py-1.5 rounded text-sm border border-gray-600 focus:outline-none focus:ring-1 focus:ring-purple-500"
                />
              </div>
              <div className="w-16">
                <label className="text-gray-500 text-xs">HP Max</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={newEnemyMaxHP}
                  onChange={(e) => { setNewEnemyMaxHP(e.target.value as any); setNewEnemyCurrentHP(e.target.value as any); }}
                  onBlur={(e) => { const n = parseInt(e.target.value); const v = isNaN(n) ? 10 : Math.max(1, n); setNewEnemyMaxHP(v); setNewEnemyCurrentHP(v); }}
                  className="w-full bg-gray-700 text-white px-2 py-1.5 rounded text-sm border border-gray-600 focus:outline-none focus:ring-1 focus:ring-purple-500"
                />
              </div>
              <div className="w-16">
                <label className="text-gray-500 text-xs">HP Att.</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={newEnemyCurrentHP}
                  onChange={(e) => setNewEnemyCurrentHP(e.target.value as any)}
                  onBlur={(e) => { const n = parseInt(e.target.value); setNewEnemyCurrentHP(isNaN(n) ? 10 : Math.max(0, n)); }}
                  className="w-full bg-gray-700 text-white px-2 py-1.5 rounded text-sm border border-gray-600 focus:outline-none focus:ring-1 focus:ring-purple-500"
                />
              </div>
              <button
                onClick={addEnemy}
                className="px-3 py-1.5 bg-purple-600 text-white rounded text-sm font-semibold hover:bg-purple-500"
              >
                +
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Damage controls */}
      <style>{`
        @keyframes fire-border {
          0%, 100% { box-shadow: 0 0 4px #ef4444, 0 0 8px #f97316, 0 0 12px rgba(249,115,22,0.3); border-color: #ef4444; }
          33% { box-shadow: 0 0 6px #f97316, 0 0 12px #eab308, 0 0 16px rgba(234,179,8,0.3); border-color: #f97316; }
          66% { box-shadow: 0 0 4px #eab308, 0 0 10px #ef4444, 0 0 14px rgba(239,68,68,0.3); border-color: #eab308; }
        }
        .fire-input {
          animation: fire-border 1.5s ease-in-out infinite;
          border-width: 2px;
        }
      `}</style>
      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
        <div className="flex items-center gap-3">
          <span className="text-gray-400 text-sm font-semibold whitespace-nowrap">
            {selectedTargetId
              ? `Bersaglio: ${combatants.find((c) => c.id === selectedTargetId)?.name || '??'}`
              : 'Seleziona un bersaglio'}
          </span>
          <input
            type="text"
            inputMode="numeric"
            value={damageInput}
            onChange={(e) => setDamageInput(e.target.value)}
            placeholder="Valore"
            className="w-24 bg-gray-700 text-white px-3 py-2 rounded-lg text-sm fire-input focus:outline-none"
            onKeyDown={(e) => e.key === 'Enter' && applyDamage()}
          />
          <button
            onClick={applyDamage}
            disabled={!selectedTargetId || !damageInput}
            className="px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            Danni
          </button>
          <button
            onClick={applyHeal}
            disabled={!selectedTargetId || !damageInput}
            className="px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            Cura
          </button>
          <div className="flex-1" />
          {!battleOutcome ? (
            <>
              <button
                onClick={() => setOutcome('victory')}
                className="px-3 py-2 bg-yellow-600 text-white rounded-lg font-semibold hover:bg-yellow-500 text-sm"
              >
                Vittoria
              </button>
              <button
                onClick={() => setOutcome('defeat')}
                className="px-3 py-2 bg-red-700 text-white rounded-lg font-semibold hover:bg-red-600 text-sm"
              >
                Sconfitta
              </button>
              <button
                onClick={() => setOutcome('draw')}
                className="px-3 py-2 bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-500 text-sm"
              >
                Pareggio
              </button>
            </>
          ) : (
            <button
              onClick={closeBattle}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-500 text-sm"
            >
              Chiudi Battaglia
            </button>
          )}
        </div>
      </div>

      {/* Effetti Magici durante battaglia attiva */}
      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
        <h3 className="text-lg font-bold text-white mb-3">✨ Effetti Magici</h3>
        <div className="grid grid-cols-3 gap-2">
          {BATTLE_EFFECTS.map((effect) => (
            <button
              key={effect.type}
              onClick={() => triggerEffect(effect.type)}
              className={`px-3 py-2 ${effect.buttonColor} text-white rounded-lg text-sm font-semibold transition-all`}
            >
              {effect.icon} {effect.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
